import { useState } from 'react';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  interest: string;
  startTime: string;
  santanaAccess: string;
  message: string;
}

export default function ContactForm() {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    interest: '',
    startTime: '',
    santanaAccess: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showOtherZonesMessage, setShowOtherZonesMessage] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Mostrar mensagem se "Outras Zonas" for selecionada
    if (name === 'santanaAccess') {
      setShowOtherZonesMessage(value === 'outras-zonas');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Validar campos obrigatórios
      if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.santanaAccess) {
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }

      // Preparar dados conforme documentação M4Track
      const dataToSend = {
        api_key: "582121bf-6661-4c70-81e0-f180f481a92b",
        nome: formData.name.trim(),
        email: formData.email.trim(),
        telefone: `+55${formData.phone.replace(/\D/g, '')}`,
        origem: "landing_page",
        "6": `Interesse: ${formData.interest || 'Não informado'}`,
        "7": `Quando pretende começar: ${formData.startTime || 'Não informado'}`,
        "8": `Local preferido: ${formData.santanaAccess}`,
        "4": formData.message.trim() || "Sem mensagem adicional"
      };

      // Verificar se está em ambiente local
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isLocalhost) {
        // Em ambiente local, simular sucesso para teste
        console.log('Ambiente local detectado. Dados que seriam enviados:', dataToSend);
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simular delay da requisição
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          interest: '',
          startTime: '',
          santanaAccess: '',
          message: ''
        });
        return;
      }

      // Enviar para M4Track
      console.log('Enviando dados para M4Track:', dataToSend);
      
      // Criar AbortController para timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 segundos timeout
      
      try {
        const response = await fetch('https://webhooks.manager01.m4track.com.br/webhook/site-vox-santana', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(dataToSend),
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        
        const success = response.ok || response.status === 200 || response.status === 201;
        
        if (success) {
          console.log('M4Track: Sucesso');
        } else {
          console.error('M4Track: Erro na resposta', response.status, response.statusText);
        }

        if (success) {
          setSubmitStatus('success');
          setFormData({
            name: '',
            email: '',
            phone: '',
            interest: '',
            startTime: '',
            santanaAccess: '',
            message: ''
          });
        } else {
          setSubmitStatus('error');
        }
      } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          console.error('Requisição cancelada por timeout');
        } else {
          console.error('Erro na requisição:', error);
        }
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contato" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Fale Conosco
          </h2>
          <p className="text-xl text-gray-600">
            Preencha o formulário e nossa equipe entrará em contato
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F19E1A] focus:border-transparent text-sm"
                  placeholder="Seu nome completo"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Seu Melhor E-mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F19E1A] focus:border-transparent text-sm"
                  placeholder="seu@email.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Telefone/WhatsApp com DDD *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <span className="text-lg mr-1">🇧🇷</span>
                    <span className="text-gray-500 text-sm">+55</span>
                  </div>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-20 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F19E1A] focus:border-transparent text-sm"
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="interest" className="block text-sm font-semibold text-gray-700 mb-2">
                  Interesse Principal
                </label>
                <select
                  id="interest"
                  name="interest"
                  value={formData.interest}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F19E1A] focus:border-transparent text-sm pr-8"
                >
                  <option value="">Selecione uma opção</option>
                  <option value="performance-corporativa">Aumentar Performance Corporativa</option>
                  <option value="eliminar-medo-tensao">Eliminar Medo/Tensão ao falar em público</option>
                  <option value="palestras-apresentacoes">Ministrar Palestras/Apresentações de impacto</option>
                  <option value="videos-atrativos">Engajar com vídeos mais atrativos</option>
                  <option value="outro-motivo">Outro motivo</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="startTime" className="block text-sm font-semibold text-gray-700 mb-2">
                Em quanto tempo você pretende começar seu desenvolvimento em oratória?
              </label>
              <select
                id="startTime"
                name="startTime"
                value={formData.startTime}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F19E1A] focus:border-transparent text-sm pr-8"
              >
                <option value="">Selecione uma opção</option>
                <option value="urgente">Urgente (o quanto antes)</option>
                <option value="proximo-mes">Para o próximo mês</option>
                <option value="ate-3-meses">Em até 3 meses</option>
                <option value="so-pesquisando">Só pesquisando, meu desenvolvimento não é prioridade</option>
              </select>
            </div>

            <div className="mb-6">
              <label htmlFor="santanaAccess" className="block text-sm font-semibold text-gray-700 mb-2">
                Em qual local de São Paulo você faria o seu treinamento presencial, pelo menos 1x por semana com horários flexíveis? *
              </label>
              <select
                id="santanaAccess"
                name="santanaAccess"
                value={formData.santanaAccess}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F19E1A] focus:border-transparent text-sm pr-8"
              >
                <option value="">Selecione uma opção</option>
                <option value="zona-norte">Zona Norte</option>
                <option value="zona-leste">Zona Leste</option>
                <option value="outras-zonas">Outras Zonas</option>
              </select>
            </div>

            {showOtherZonesMessage && (
              <div className="mb-6 p-6 bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-[#F19E1A] rounded-xl shadow-sm">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#F19E1A] rounded-full flex items-center justify-center mr-4">
                    <i className="ri-information-line text-white text-lg"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center">
                      <span className="bg-gradient-to-r from-[#F19E1A] to-[#E08A00] bg-clip-text text-transparent">
                        Informação Importante
                      </span>
                    </h3>
                    <div className="space-y-2.5">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        Fazemos parte da <span className="font-semibold text-[#F19E1A]">unidade Vox Zona Norte</span> e 
                        <span className="font-semibold text-[#F19E1A]"> Vox Zona Leste</span> em São Paulo.
                      </p>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Desta forma, o mais apropriado seria que conversasse com uma unidade mais perto de você. 
                        Recomendamos que busque no Google e desde já, 
                        <span className="font-medium text-[#E08A00]"> te agradeço por querer fazer parte da Família Vox!</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                Mensagem
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F19E1A] focus:border-transparent text-sm resize-none"
                placeholder="Conte-nos mais sobre seus objetivos e expectativas..."
              ></textarea>
              <div className="text-right text-xs text-gray-500 mt-1">
                {formData.message.length}/500 caracteres
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || showOtherZonesMessage}
              className={`w-full font-semibold py-4 px-6 rounded-lg transition-colors duration-200 disabled:cursor-not-allowed whitespace-nowrap ${
                showOtherZonesMessage 
                  ? 'bg-gray-400 text-gray-600' 
                  : 'bg-[#F19E1A] hover:bg-[#E08A00] text-white disabled:opacity-50'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Enviando...
                </span>
              ) : showOtherZonesMessage ? (
                <span className="flex items-center justify-center">
                  <i className="ri-close-circle-line mr-2"></i>
                  Outras Zonas Não Aceitas
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <i className="ri-send-plane-line mr-2"></i>
                  Enviar Mensagem
                </span>
              )}
            </button>

            {submitStatus === 'success' && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-green-800">Mensagem enviada com sucesso! Entraremos em contato em breve.</span>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-600 mr-2"></i>
                  <span className="text-red-800">Erro ao enviar mensagem. Tente novamente.</span>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
